"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Progress from "../atoms/Progress";

const Loader = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [progress, setProgress] = useState(0);
  const [hidden, setHidden] = useState(true);

  const isLoadingRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Moment où le loader a démarré
  const startTimeRef = useRef<number>(0);

  const clearTimers = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startLoading = () => {
    if (isLoadingRef.current) return;
    clearTimers();

    isLoadingRef.current = true;
    startTimeRef.current = Date.now();

    setHidden(false);
    setProgress(10);

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 30) {
          return Math.min(prev + 3, 30);
        }

        if (prev < 60) {
          return Math.min(prev + 1.5, 60);
        }

        if (prev < 80) {
          return Math.min(prev + 0.5, 80);
        }

        if (prev < 90) {
          return Math.min(prev + 0.2, 90);
        }

        return 90;
      });
    }, 200);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        e.ctrlKey ||
        e.metaKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }

      const target = e.target as HTMLElement;
      const link = target.closest("a");

      if (!link) return;

      if (link.target === "_blank") return;
      if (link.hasAttribute("download")) return;

      const href = link.getAttribute("href");

      if (!href) return;

      if (
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:")
      ) {
        return;
      }

      const url = new URL(link.href, window.location.href);
      const currentUrl = new URL(window.location.href);

      if (
        url.origin === currentUrl.origin &&
        (
          url.pathname !== currentUrl.pathname ||
          url.search !== currentUrl.search
        )
      ) {
        startLoading();
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      clearTimers();
    };
  }, []);

  useEffect(() => {
    if (!isLoadingRef.current) {
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const elapsed = Date.now() - startTimeRef.current;

    // Le loader doit rester visible au moins 500 ms
    const minimumDuration = 500;

    const remainingTime = Math.max(
      minimumDuration - elapsed,
      0
    );

    timeoutRef.current = setTimeout(() => {

      setProgress(100);

      setTimeout(() => {
        setHidden(true);
        setProgress(0);

        isLoadingRef.current = false;
      }, 250);
    }, remainingTime);

  }, [pathname, searchParams]);

  if (hidden) {
    return null;
  }

  return (
    <Progress
      value={progress}
      className=" [&>*]:bg-primaryGreen z-[999999] fixed top-0 left-0 w-full transition-opacity duration-300 [&>div]:transition-all [&>div]:duration-300 [&>div]:ease-out
      "
    />
  );
};

export default Loader;